import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Search, X } from "lucide-react";

import Button from "../ui/Button";
import PosterPlaceholder from "../ui/PosterPlaceholder";
import { cn } from "../../utils/classNames";
import {
  buildShowPickerSuggestions,
  DEFAULT_MAX_SHOW_PICKER_SUGGESTIONS,
} from "./showPickerSuggestions";

const alwaysEligible = () => true;
const noPreference = () => 0;
const noMeta = () => "";
const noDestinationLabel = () => "";
const noHref = () => "";

const ShowPicker = ({
  id,
  idPrefix = "show-picker",
  label,
  placeholder,
  selectedShow = null,
  shows = [],
  excludeSlug = "",
  disabled = false,
  loading = false,
  maxSuggestions = DEFAULT_MAX_SHOW_PICKER_SUGGESTIONS,
  isEligible = alwaysEligible,
  getPreferenceScore = noPreference,
  getSuggestionMeta = noMeta,
  getDestinationLabel = noDestinationLabel,
  getHref = noHref,
  noResultsMessage = "",
  getNoResultsHref = noHref,
  onSelect,
  onClear,
}) => {
  const reactId = useId();
  const inputId = id || `${idPrefix}-${reactId}`;
  const listboxId = `${inputId}-listbox`;
  const statusId = `${inputId}-status`;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const fallbackRef = useRef(null);
  const containerRef = useRef(null);
  const optionRefs = useRef([]);

  const suggestions = useMemo(() => {
    if (selectedShow) return [];
    return buildShowPickerSuggestions({
      shows,
      query,
      excludeSlug,
      maxSuggestions,
      isEligible,
      getPreferenceScore,
    });
  }, [shows, query, excludeSlug, maxSuggestions, isEligible, getPreferenceScore, selectedShow]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const showsSuggestions = open && !selectedShow && suggestions.length > 0;
  const showsNoResults = open && !selectedShow && Boolean(query.trim())
    && suggestions.length === 0 && Boolean(noResultsMessage);
  const resolvedActiveIndex = suggestions.length > 0
    ? Math.min(Math.max(activeIndex, 0), suggestions.length - 1)
    : -1;

  const commitSelection = (show, event) => {
    if (!show?.slug) return;
    setQuery("");
    setOpen(false);
    onSelect?.(show, event);
  };

  const handleClear = () => {
    setQuery("");
    setOpen(false);
    onClear?.();
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleKeyDown = (event) => {
    if (selectedShow) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (!suggestions.length) return;
      setOpen(true);
      setActiveIndex((current) => (current + 1) % suggestions.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (!suggestions.length) return;
      setOpen(true);
      setActiveIndex((current) => (current <= 0 ? suggestions.length - 1 : current - 1));
      return;
    }
    if (event.key === "Enter") {
      if (showsNoResults && fallbackRef.current) {
        event.preventDefault();
        fallbackRef.current.click();
        return;
      }
      if (showsSuggestions && resolvedActiveIndex >= 0 && suggestions[resolvedActiveIndex]) {
        event.preventDefault();
        const activeOption = optionRefs.current[resolvedActiveIndex];
        if (activeOption?.tagName === "A") {
          activeOption.click();
        } else {
          commitSelection(suggestions[resolvedActiveIndex], event);
        }
      }
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      <label htmlFor={inputId} className="text-body-small font-semibold text-foreground">
        {label}
      </label>

      {selectedShow ? (
        <div
          className="flex h-12 items-center gap-3 rounded-lg border border-accent-gold/70 bg-card pl-2 pr-1 shadow-card-base"
          aria-live="polite"
        >
          <div className="h-10 w-7 shrink-0 overflow-hidden rounded-sm bg-muted">
            {selectedShow.poster ? (
              <img
                src={selectedShow.poster}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <PosterPlaceholder showId={selectedShow.slug} className="h-full w-full" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-body-small font-semibold text-foreground">{selectedShow.title}</p>
            {getSuggestionMeta(selectedShow) ? (
              <p className="truncate text-body-xs text-muted-foreground">
                {getSuggestionMeta(selectedShow)}
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClear}
            aria-label={`Clear ${label}`}
            disabled={disabled}
            className="min-h-10 min-w-10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ) : (
        <div className="relative">
          {/* The icon centres on the input alone, not on the input plus the
              no-results message below it. */}
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-primary"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              inputMode="search"
              autoComplete="off"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={showsSuggestions}
              aria-controls={listboxId}
              aria-activedescendant={showsSuggestions && resolvedActiveIndex >= 0 ? `${listboxId}-${resolvedActiveIndex}` : undefined}
              aria-describedby={statusId}
              value={query}
              disabled={disabled || loading}
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
                setActiveIndex(0);
              }}
              onFocus={() => {
                setOpen(true);
                if (suggestions.length > 0 && activeIndex < 0) setActiveIndex(0);
              }}
              onKeyDown={handleKeyDown}
              placeholder={loading ? "Loading shows…" : placeholder}
              className="h-12 w-full rounded-lg border border-accent-gold/70 bg-background/85 pl-11 pr-3 text-body text-foreground outline-hidden transition focus:border-primary focus:ring-2 focus:ring-primary/25 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
          {showsSuggestions ? (
            <ul
              id={listboxId}
              role="listbox"
              className="absolute z-30 mt-2 max-h-80 w-full overflow-auto rounded-lg border border-accent-gold/70 bg-card p-1 shadow-xl"
            >
              {suggestions.map((show, index) => {
                const active = index === resolvedActiveIndex;
                const meta = getSuggestionMeta(show);
                const destinationLabel = getDestinationLabel(show);
                const href = getHref(show);
                const optionProps = {
                  id: `${listboxId}-${index}`,
                  role: "option",
                  "aria-selected": active,
                  onMouseEnter: () => setActiveIndex(index),
                  onMouseDown: (event) => event.preventDefault(),
                  onClick: (event) => commitSelection(show, event),
                  className: cn(
                    "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition",
                    active ? "bg-primary text-primary-foreground" : "hover:bg-accent-gold/10"
                  ),
                };
                const optionContent = (
                  <>
                    <span className="h-10 w-7 shrink-0 overflow-hidden rounded-sm bg-muted">
                      {show.poster ? (
                        <img
                          src={show.poster}
                          alt=""
                          aria-hidden="true"
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <PosterPlaceholder showId={show.slug} className="h-full w-full" />
                      )}
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-body-small font-semibold">{show.title}</span>
                      {meta ? (
                        <span
                          className={cn(
                            "truncate text-body-xs",
                            active ? "text-primary-foreground/85" : "text-muted-foreground"
                          )}
                        >
                          {meta}
                        </span>
                      ) : null}
                    </span>
                    {destinationLabel ? (
                      <span
                        className={cn(
                          "shrink-0 text-body-xs font-semibold",
                          active ? "text-primary-foreground/85" : "text-muted-foreground"
                        )}
                      >
                        {destinationLabel}
                      </span>
                    ) : null}
                  </>
                );

                return (
                  <li key={show.slug}>
                    {href ? (
                      <a
                        ref={(node) => { optionRefs.current[index] = node; }}
                        href={href}
                        {...optionProps}
                      >
                        {optionContent}
                      </a>
                    ) : (
                      <button
                        ref={(node) => { optionRefs.current[index] = node; }}
                        type="button"
                        {...optionProps}
                      >
                        {optionContent}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : showsNoResults ? (
            <p className="mt-2 text-body-small text-muted-foreground">
              {noResultsMessage}{" "}
              {getNoResultsHref(query.trim()) && (
                <a ref={fallbackRef} href={getNoResultsHref(query.trim())} className="font-semibold text-primary underline underline-offset-4">
                  Search the archive for “{query.trim()}”
                </a>
              )}
            </p>
          ) : null}
        </div>
      )}

      <p id={statusId} className="sr-only" aria-live="polite">
        {loading
          ? "Loading shows."
          : selectedShow
            ? `Selected ${selectedShow.title}.`
            : query
              ? suggestions.length === 0 && noResultsMessage
                ? noResultsMessage
                : `${suggestions.length} suggestion${suggestions.length === 1 ? "" : "s"}.`
              : "Type to search shows."}
      </p>
    </div>
  );
};

ShowPicker.propTypes = {
  id: PropTypes.string,
  idPrefix: PropTypes.string,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  selectedShow: PropTypes.object,
  shows: PropTypes.arrayOf(PropTypes.object),
  excludeSlug: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  maxSuggestions: PropTypes.number,
  isEligible: PropTypes.func,
  getPreferenceScore: PropTypes.func,
  getSuggestionMeta: PropTypes.func,
  getDestinationLabel: PropTypes.func,
  getHref: PropTypes.func,
  noResultsMessage: PropTypes.string,
  getNoResultsHref: PropTypes.func,
  onSelect: PropTypes.func,
  onClear: PropTypes.func,
};

export default ShowPicker;
